'use client';

import { c as _c } from "react/compiler-runtime";
import { type RefObject, useEffect } from 'react';

// Room left above the sheet when the keyboard leaves it little space.
const TOP_GAP_PX = 24;

// Keeps the sheet's bottom edge on the top of the keyboard. The drawer's own
// keyboard handling (vaul's `repositionInputs`) must be off for this sheet: it
// pins the height and only accounts for the keyboard, so on iOS, which scrolls
// the page when a field is focused, the sheet floated above the keyboard and
// jumped as the two fought. Here the position comes from the visual viewport
// alone, so the same viewport always gives the same position, and the height
// is left to the content, capped to what is visible so a tall step scrolls.
// The layout box (offsetTop/offsetHeight) is measured, not the bounding rect,
// so vaul's open animation and drag, which use transforms, don't skew it.
export function useKeepSheetAboveKeyboard(contentRef) {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] !== contentRef) {
    t0 = () => {
      const viewport = window.visualViewport;
      const content = contentRef.current;
      const drawer = content?.closest("[data-vaul-drawer]");
      if (!viewport || !content || !drawer) {
        return;
      }
      const update = function update() {
        const vv = window.visualViewport;
        if (!vv || !drawer) {
          return;
        }
        const isKeyboardOpen = vv.height < window.innerHeight;
        if (!isKeyboardOpen) {
          drawer.style.bottom = "";
          drawer.style.maxHeight = "";
          return;
        }
        const overlap = drawer.offsetTop + drawer.offsetHeight - (vv.offsetTop + vv.height);
        const bottom = parseFloat(drawer.style.bottom) || 0;
        drawer.style.bottom = `${Math.max(bottom + overlap, 0)}px`;
        drawer.style.maxHeight = `${vv.height - TOP_GAP_PX}px`;
        const focused = document.activeElement;
        if (focused instanceof HTMLElement && drawer.contains(focused)) {
          focused.scrollIntoView({
            block: "nearest"
          });
        }
      };
      let frame = 0;
      const scheduleUpdate = function scheduleUpdate() {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(update);
      };
      viewport.addEventListener("resize", scheduleUpdate);
      viewport.addEventListener("scroll", scheduleUpdate);
      return () => {
        cancelAnimationFrame(frame);
        viewport.removeEventListener("resize", scheduleUpdate);
        viewport.removeEventListener("scroll", scheduleUpdate);
        drawer.style.bottom = "";
        drawer.style.maxHeight = "";
      };
    };
    t1 = [contentRef];
    $[0] = contentRef;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
}